import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Form,
  Input,
  Button,
  ColorPicker,
  Typography,
  List,
  Divider,
  Drawer,
  Row,
  Col,
  message,
  Select,
} from "antd";
import {
  SettingOutlined,
  EnvironmentOutlined,
  SaveOutlined,
  PlusOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import type { Department, Location } from "../../../../../types/location";
import { BranchOperation } from "../../../../../serviceoperation/BranchOperation";
import { CompanyContext } from "../../../../../contexts/company-context";
import { State } from "country-state-city";
import { DepartmentOperation } from "../../../../../serviceoperation/DepartmentOperation";

const { Title } = Typography;

interface LocationSettingsProps {
  onClose: () => void;
  onCreate: (value: Location) => void;
  location: Location;
}

const LocationSettings: React.FC<LocationSettingsProps> = ({
  onClose,
  onCreate,
  location,
}) => {
  const { selectedCompany } = useContext(CompanyContext);
  const departmentOps = useMemo(() => new DepartmentOperation(), []);
  const [form] = Form.useForm();
  const [deptForm] = Form.useForm();
  const [open, setOpen] = useState(true);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [locationz, setLocationz] = useState<Location>(location);
  const [departments, setDepartments] = useState<Department[]>(
    locationz.departments
  );
  const [departConfirmLoading, setDepartConfirmLoading] =
    useState<boolean>(false);
  //const [editingId, setEditingId] = useState<React.Key | null>(null);
  //const [editingName, setEditingName] = useState<string>("");

  const onFinishLocation = (values: any) => {
    setSaveLoading(true);
    const branchOps = new BranchOperation();
    branchOps
      .putBranchAsync(
        selectedCompany?.corporationId as number,
        Number(locationz.id),
        {
          branchName: values.name,
          branchCode: "",
          branchDescription: values.description,
          addressLine1: values.streetAddress,
          city: values.city,
          state: values.state,
          zip: values.zip,
          country: "US",
          timeZoneId: values.timezone,
          color: values.color?.toHexString?.() ?? locationz.color,
        }
      )
      .then((x) => {
        if (x.statusCode === 200) {
          message.success("Successfully processed your request!");
          setSaveLoading(false);

          const updatedLocation = {
            ...locationz, // spread existing fields
            name: values.name,
            description: values.description,
            address: values.streetAddress,
            city: values.city,
            state: values.state,
            zipCode: values.zip,
            timezone: values.timezone,
            color: values.color?.toHexString?.() ?? locationz.color,
          };

          onCreate(updatedLocation);
          setLocationz(updatedLocation);
        } else {
          if (x.statusCode == 402) {
            message.error(x.statusMessage);
          } else {
            message.error(
              "An error has occured while processing your request!"
            );
          }
          setSaveLoading(false);
        }
      });
  };

  const onAddDepartment = (values: any) => {
    setDepartConfirmLoading(true);
    departmentOps
      .postDepartmentAsync(
        selectedCompany?.corporationId as number,
        Number(locationz.id),
        {
          name: values.department,
        }
      )
      .then((x) => {
        if (x.statusCode === 201) {
          message.success("Successfully created a department!");
          form.resetFields();
          setDepartConfirmLoading(false);
          const newDept: Department = {
            id: x.data!.Id,
            name: values.department,
            description: values.department,
            employeeCount: 0,
            createdAt: "",
          };
          const newDepartments = [...departments, newDept];
          setDepartments(newDepartments);

          const updatedLocation = {
            ...locationz,
            departments: newDepartments,
          };

          onCreate(updatedLocation);
          setLocationz(updatedLocation);
          deptForm.resetFields();
        } else {
          if (x.statusCode == 402) {
            message.error(x.statusMessage);
          } else {
            message.error(
              "An error has occured while processing your request!"
            );
          }
          setDepartConfirmLoading(false);
        }
      });
  };

  /*const onDelete = (id: React.Key) => {
    setDepartments((prev) => prev.filter((d) => d.id !== id));
    message.success("Department removed");
  };

  const onEdit = (dept: Department) => {
    setEditingId(dept.id);
    setEditingName(dept.name);
  };

  const onSaveEdit = (id: React.Key) => {
    setDepartments((prev) =>
      prev.map((d) => (d.id === id ? { ...d, name: editingName } : d))
    );
    setEditingId(null);
    message.success("Department updated");
  };*/

  return (
    <Drawer
      title={
        <>
          <SettingOutlined style={{ marginRight: 8 }} /> Location Settings
        </>
      }
      width={560}
      open={open}
      onClose={() => onClose()}
      bodyStyle={{ paddingBottom: 80 }}
    >
      {/* Location Details Form */}
      <Title level={5}>
        <HomeOutlined style={{ marginRight: 8 }} /> Location Info
      </Title>
      <Form layout="vertical" form={form} onFinish={onFinishLocation}>
        <Form.Item
          label="Name"
          name="name"
          initialValue={locationz.name}
          rules={[{ required: true, message: "Please enter location name" }]}
        >
          <Input placeholder="Please enter location name" />
        </Form.Item>

        <Form.Item
          initialValue={locationz.description}
          label="Description"
          name="description"
        >
          <Input.TextArea rows={3} placeholder="Branch description" />
        </Form.Item>

        <Form.Item
          label="Street Address"
          name="streetAddress"
          initialValue={locationz.address}
        >
          <Input placeholder="123 Main St" prefix={<EnvironmentOutlined />} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item initialValue={locationz.city} label="City" name="city">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              initialValue={locationz.state}
              name="state"
              label="State"
              rules={[{ required: true, message: "Please select state" }]}
            >
              <Select
                showSearch
                placeholder="Select State"
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={State.getStatesOfCountry("US").map((state) => {
                  return {
                    value: state.name,
                    label: state.name,
                  };
                })}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item initialValue={locationz.zipCode} label="ZIP" name="zip">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="TimeZone"
          name="timezone"
          initialValue={locationz.timezone}
        >
          <Input disabled />
        </Form.Item>

        <Form.Item
          label="Color"
          name="color"
          initialValue={locationz.color ?? "#1890ff"}
        >
          <ColorPicker />
        </Form.Item>

        <Form.Item>
          <Button
            loading={saveLoading}
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
          >
            Save Preferences
          </Button>
        </Form.Item>
      </Form>

      <Divider />

      {/* Departments Section */}
      <Title level={5}>
        <PlusOutlined style={{ marginRight: 8 }} /> Departments
      </Title>

      <List
        size="small"
        dataSource={departments}
        locale={{ emptyText: "No departments added yet." }}
        renderItem={(dept) => (
          <List.Item
            style={{ display: "flex", alignItems: "center", gap: 8 }}
            /*actions={
              editingId === dept.id
                ? [
                    <Button
                      icon={<SaveOutlined />}
                      type="link"
                      onClick={() => onSaveEdit(dept.id)}
                    />,
                    <Button
                      icon={<CloseOutlined />}
                      type="link"
                      onClick={() => setEditingId(null)}
                    />,
                  ]
                : [
                    <Button
                      icon={<EditOutlined />}
                      type="link"
                      onClick={() => onEdit(dept)}
                    />,
                    <Button
                      icon={<DeleteOutlined />}
                      type="link"
                      danger
                      onClick={() => onDelete(dept.id)}
                    />,
                  ]
            }*/
          >
            {/*{editingId === dept.id ? (
              <Input
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onPressEnter={() => onSaveEdit(dept.id)}
              />
            ) : (
              <span>{dept.name}</span>
            )}*/}
            <span>{dept.name}</span>
          </List.Item>
        )}
      />

      <Form
        form={deptForm}
        layout="vertical"
        onFinish={onAddDepartment}
        style={{ marginTop: 24 }}
      >
        <Form.Item
          label="New Department"
          name="department"
          rules={[{ required: true, message: "Enter department name" }]}
        >
          <Input placeholder="e.g. Marketing" />
        </Form.Item>
        <Form.Item>
          <Button
            loading={departConfirmLoading}
            type="dashed"
            htmlType="submit"
            icon={<PlusOutlined />}
            block
          >
            Add Department
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default LocationSettings;
